import { useCallback, useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { RecurringArrow } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  ClientsKeys,
  getLicenseeClients,
} from "@/features/clients/api/clients";
import { Client } from "@/features/clients/types/clients";
import {
  allowedPaymentMethods,
  createDraftInvoice,
  createInvoiceItem,
  deleteInvoiceItem,
  finalizeInvoice,
  getInvoiceDetails,
  InvoicesKeys,
  patchInvoiceHeader,
  patchInvoiceItem,
} from "@/features/invoices/api/invoices";
import { useCreateRecurringInvoice } from "@/features/invoices/hooks/useRecurringInvoices";
import { track } from "@/utils/mixpanel";
import { filterPaymentMethodsByCurrency } from "@/utils/paymentMethods";

import CurrencySelector from "./form/CurrencySelector";
import CustomerInformation from "./form/CustomerInformation";
import DueDatePicker from "./form/DueDatePicker";
import InvoiceCustomFields from "./form/InvoiceCustomFields";
import InvoiceNotesField from "./form/InvoiceNotesField";
import LineItemsEditor from "./form/LineItemsEditor";
import PaymentMethodSelector from "./form/PaymentMethodSelector";
import RecurringInvoiceConfig from "./form/RecurringInvoiceConfig";
import { invoiceFormSchema, InvoiceFormValues } from "./form/types";
import InvoiceDrawer from "./InvoiceDrawer";
import InvoicePdfPreviewSheet from "./InvoicePdfPreviewSheet";

interface InvoiceFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: () => void;
  existingInvoiceId?: string;
  onViewRecurringInvoice?: (recurringInvoiceId: string) => void;
}

const InvoiceFormSheet = ({
  open,
  onOpenChange,
  onSubmit,
  existingInvoiceId: initialExistingInvoiceId,
  onViewRecurringInvoice,
}: InvoiceFormSheetProps) => {
  const queryClient = useQueryClient();
  const [draftInvoiceId, setDraftInvoiceId] = useState<string | null>(null);
  const [invoiceItemId, setInvoiceItemId] = useState<string | null>(null);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [customerSelected, setCustomerSelected] = useState<Client | null>(null);
  const [currencySelected, setCurrencySelected] = useState<string | null>(null);
  const [isCreatingLineItem, setIsCreatingLineItem] = useState(false);
  // STA-682: covers the entire submit flow (syncLineItems + patchHeader +
  // finalize) so the button shows loading instantly on click, not just when
  // the final finalize mutation fires.
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add state for PDF preview modal
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  // Step navigation state
  const [activeTab, setActiveTab] = useState<"basics" | "details">("basics");
  // Add state to track the existingInvoiceId that can be updated during the flow
  const [existingInvoiceId, setExistingInvoiceId] = useState<
    string | undefined
  >(initialExistingInvoiceId);
  // Add state to store the finalized invoice data directly
  const [finalizedInvoice, setFinalizedInvoice] = useState<any>(null);

  // Refs to track previous values
  const prevAmount = useRef<number>(0);
  const prevDescription = useRef<string>("");
  const itemBlurTimerRef = useRef<NodeJS.Timeout | null>(null);

  // STA-682: snapshot of line-item IDs + contents as last loaded from the
  // backend. Used on submit to compute the diff (create / patch / delete)
  // against the form's current lineItems.
  const baselineLineItemsRef = useRef<
    Array<{ id: string; description: string; amount: number }>
  >([]);

  // Create a ref to store the form values for use in the mutation
  const formValuesRef = useRef<InvoiceFormValues | null>(null);

  // Invoice type state (one-time vs recurring)
  const [invoiceType, setInvoiceType] = useState<"one-time" | "recurring">(
    "one-time",
  );
  // Store recurring config separately to persist when toggling
  const [recurringConfig, setRecurringConfig] = useState({
    frequency: 1,
    frequencyUnit: "months" as const,
    dueDateType: "immediate" as "immediate" | "net_days",
    dueDateDays: 0,
    endCondition: "never" as "never" | "after_count",
    endAfterCount: null as number | null,
  });

  // Set default date to end of today (11:59:59 PM) to ensure it's in the future
  const getEndOfToday = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  };

  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: {
      customerId: "",
      currency: "USD",
      amount: 0,
      description: "",
      dueDate: getEndOfToday(),
      paymentMethods: [],
      poNumber: "",
      projectName: "",
      notes: "",
      lineItems: [{ description: "", amount: 0 }],
      invoiceType: "one-time",
      frequency: 1,
      frequencyUnit: "months",
      dueDateType: "immediate",
      dueDateDays: 0,
      endCondition: "never",
      endAfterCount: null,
    },
    mode: "onChange",
  });

  // Fetch existing invoice data if existingInvoiceId is provided
  const { data: existingInvoice, isLoading: isLoadingInvoice } = useQuery({
    queryKey: ["invoice", existingInvoiceId],
    queryFn: () =>
      existingInvoiceId ? getInvoiceDetails(existingInvoiceId) : null,
    enabled: !!existingInvoiceId && open,
  });

  // Use the finalized invoice data if available, otherwise use the fetched data
  const currentInvoice = finalizedInvoice || existingInvoice;

  // Add customers data loading to ensure it's always available
  const { data: clients = [] } = useQuery({
    queryKey: [ClientsKeys.Clients],
    queryFn: getLicenseeClients,
    enabled: open,
  });

  // Update the useEffect to only reset for new invoices and not interfere with viewing existing ones
  useEffect(() => {
    if (open) {
      setActiveTab("basics");
      // Only reset when the modal opens for a new invoice
      if (!initialExistingInvoiceId) {
        console.log("Resetting form for new invoice");
        // Reset form state for new invoice
        form.reset({
          customerId: "",
          currency: "USD",
          amount: 0,
          description: "",
          dueDate: getEndOfToday(),
          paymentMethods: [],
          poNumber: "",
          projectName: "",
          notes: "",
          lineItems: [{ description: "", amount: 0 }],
          invoiceType: "one-time",
          frequency: recurringConfig.frequency,
          frequencyUnit: recurringConfig.frequencyUnit,
          dueDateType: recurringConfig.dueDateType,
          dueDateDays: recurringConfig.dueDateDays,
          endCondition: recurringConfig.endCondition,
          endAfterCount: recurringConfig.endAfterCount,
        });

        // Reset component state
        setDraftInvoiceId(null);
        setInvoiceItemId(null);
        setCustomerSelected(null);
        setCurrencySelected(null);
        setFinalizedInvoice(null);
        setExistingInvoiceId(initialExistingInvoiceId);
        setInvoiceType("one-time");
        baselineLineItemsRef.current = [];
      } else {
        console.log("Opening existing invoice, not resetting form");
        // For existing invoices, just make sure we have the correct ID
        setExistingInvoiceId(initialExistingInvoiceId);
      }
    }
  }, [open, initialExistingInvoiceId, form]);

  // Handle existing invoice data
  useEffect(() => {
    if (!currentInvoice || !open) return;

    // STA-682: load ALL line items (not just the first) so multi-line drafts
    // round-trip correctly when reopened.
    const rawLines = currentInvoice.lines?.data ?? [];
    const loadedLineItems = rawLines.length
      ? rawLines.map((item: any) => ({
          id: item.id,
          description: item.description || "",
          amount: item.amount ? item.amount / 100 : 0,
        }))
      : [{ description: "", amount: 0 }];

    const firstLine = rawLines[0];
    if (firstLine) {
      prevDescription.current = firstLine.description || "";
      prevAmount.current = firstLine.amount ? firstLine.amount / 100 : 0;
    }
    // Snapshot baseline so syncLineItems can diff on submit.
    baselineLineItemsRef.current = rawLines.map((item: any) => ({
      id: item.id,
      description: item.description || "",
      amount: item.amount ? item.amount / 100 : 0,
    }));

    // Set form values one by one to ensure each field gets updated
    if (currentInvoice.customer) {
      form.setValue("customerId", currentInvoice.customer);

      const customer = clients.find((c) => c.id === currentInvoice.customer);
      if (customer) {
        setCustomerSelected(customer);
      } else {
        setCustomerSelected({ id: currentInvoice.customer } as Client);
      }
    }

    if (currentInvoice.currency) {
      const currencyValue = currentInvoice.currency.toUpperCase();
      form.setValue("currency", currencyValue);
      setCurrencySelected(currencyValue);
    }

    form.setValue("lineItems", loadedLineItems);
    const computedTotal = loadedLineItems.reduce(
      (sum, li) => sum + (Number(li.amount) || 0),
      0,
    );
    form.setValue(
      "amount",
      computedTotal ||
        (currentInvoice.subtotal ? currentInvoice.subtotal / 100 : 0),
    );

    // STA-682: pull PO / Project / Notes off the raw Stripe invoice. custom_fields
    // is a typed array; notes falls back to the legacy top-level `description`
    // field so existing invoices that used that field still render their memo.
    const customFields: Array<{ name: string; value: string }> =
      currentInvoice.custom_fields ?? [];
    const poField = customFields.find((f) => f.name === "PO Number");
    const projectField = customFields.find((f) => f.name === "Project");
    form.setValue("poNumber", poField?.value || "");
    form.setValue("projectName", projectField?.value || "");
    form.setValue(
      "notes",
      currentInvoice.footer || currentInvoice.description || "",
    );

    if (currentInvoice.due_date) {
      const dueDate = new Date(currentInvoice.due_date * 1000);
      form.setValue("dueDate", dueDate);
    } else {
      form.setValue("dueDate", getEndOfToday());
    }

    if (currentInvoice.payment_settings?.payment_method_types) {
      form.setValue(
        "paymentMethods",
        currentInvoice.payment_settings.payment_method_types,
      );
    }

    setDraftInvoiceId(currentInvoice.id);
    // Keep track of the first line item's ID for the legacy single-item
    // patch path. Multi-item sync uses the per-index IDs inside `lineItems`.
    setInvoiceItemId(firstLine?.id ?? null);

    setTimeout(() => {
      form.trigger();
    }, 500);
  }, [currentInvoice, open, clients, form]);

  // Watch for currency, due date, and payment methods changes
  const currency = form.watch("currency");
  const selectedPaymentMethods = form.watch("paymentMethods") as string[];
  const customerId = form.watch("customerId");
  const amount = form.watch("amount");
  const dueDate = form.watch("dueDate");

  // Handle customer selection
  useEffect(() => {
    if (customerId && !customerSelected && !existingInvoiceId) {
      setCustomerSelected({ id: customerId } as Client);
    }
  }, [customerId, customerSelected, existingInvoiceId]);

  // Handle currency selection
  useEffect(() => {
    if (currency && customerSelected && !currencySelected && !draftInvoiceId) {
      setCurrencySelected(currency.toUpperCase());
    }
  }, [currency, customerSelected, currencySelected, draftInvoiceId, form]);

  const createRecurringInvoiceMutation = useCreateRecurringInvoice();

  const { mutate: createInvoice, isPending } = useMutation({
    mutationFn: (values: InvoiceFormValues) => {
      if (!draftInvoiceId) {
        throw new Error("No draft invoice ID available");
      }
      formValuesRef.current = values;
      return finalizeInvoice(draftInvoiceId);
    },
    onSuccess: (data) => {
      toast.dismiss("finalizing");
      const action = existingInvoiceId ? "updated" : "created";
      toast.success(`Invoice ${action} successfully!`);

      track("invoice_finalized", {
        invoice_id: data.id,
        invoice_number: data.number,
        amount: data.amount,
        currency: data.currency,
        customer_id: data.customer,
        status: data.status,
        is_update: !!existingInvoiceId,
      });

      queryClient.invalidateQueries({ queryKey: [InvoicesKeys.Invoices] });

      if (existingInvoiceId) {
        queryClient.invalidateQueries({
          queryKey: ["invoice", existingInvoiceId],
        });
      } else if (data && data.id) {
        setFinalizedInvoice(data);
        setDraftInvoiceId(data.id);
        setExistingInvoiceId(data.id);
      }

      if (onSubmit && formValuesRef.current) {
        onSubmit();
      }
    },
    onError: () => {
      toast.dismiss("finalizing");
      toast.error("Failed to finalize invoice");
      setIsSubmitting(false);
    },
  });

  const { mutate: createItem } = useMutation({
    mutationFn: createInvoiceItem,
    onMutate: () => {
      setIsCreatingLineItem(true);
    },
    onSuccess: (data) => {
      setInvoiceItemId(data.id);
      setIsCreatingLineItem(false);
      queryClient.invalidateQueries({ queryKey: [InvoicesKeys.Invoices] });
      track("line_item_created", {
        invoice_id: draftInvoiceId,
        item_id: data.id,
      });
    },
    onError: (error) => {
      setIsCreatingLineItem(false);
      console.error("Failed to create invoice item:", error);
      toast.error("Failed to create invoice line item");
    },
  });

  const { mutate: patchItem, isPending: isPatchingItem } = useMutation({
    mutationFn: ({
      invoiceItemId,
      amountCents,
      description,
    }: {
      invoiceItemId: string;
      amountCents?: number;
      description?: string;
    }) => patchInvoiceItem(invoiceItemId, { amountCents, description }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [InvoicesKeys.Invoices] });
    },
    onError: (error) => {
      console.error("Failed to update invoice item:", error);
      toast.error("Failed to update invoice line item");
    },
  });

  const { mutate: createDraftInvoiceMutation } = useMutation({
    mutationFn: createDraftInvoice,
    onSuccess: (data) => {
      setDraftInvoiceId(data.id);
      if (data.items && data.items.length > 0) {
        setInvoiceItemId(data.items[0].id);
        if (data.items[0].amount) {
          prevAmount.current = data.items[0].amount / 100;
          form.setValue("amount", data.items[0].amount / 100);
        }
        if (data.items[0].description) {
          prevDescription.current = data.items[0].description;
          form.setValue("description", data.items[0].description);
        }
      }
      setIsCreatingDraft(false);
      queryClient.invalidateQueries({ queryKey: [InvoicesKeys.Invoices] });
      toast.success("Draft invoice created");
      track("draft_invoice_created", {
        invoice_id: data.id,
        customer_id: data.customer,
        currency: data.currency,
      });
    },
    onError: () => {
      setIsCreatingDraft(false);
      toast.error("Failed to create draft invoice");
    },
  });

  const { data: paymentMethods } = useQuery({
    queryKey: [InvoicesKeys.AllowedPaymentMethods],
    queryFn: allowedPaymentMethods,
    initialData: [],
  });

  // STA-682: memoize so LineItemsEditor's useEffect doesn't infinite-loop.
  // An inline arrow here would create a new reference on every render →
  // useEffect fires → form.setValue → re-render → new ref → infinite.
  const handleLineItemsTotalChange = useCallback(
    (total: number) => {
      form.setValue("amount", total);
    },
    [form],
  );

  const handleItemBlur = () => {
    // Only auto-save if we already have a draft invoice (step 2)
    if (!draftInvoiceId || isCreatingLineItem) return;

    if (itemBlurTimerRef.current) {
      clearTimeout(itemBlurTimerRef.current);
    }

    itemBlurTimerRef.current = setTimeout(() => {
      const { amount, description, currency } = form.getValues();
      const amountCents = Math.round(amount * 100);

      if (
        (amountCents === prevAmount.current &&
          description === prevDescription.current) ||
        !amountCents ||
        !description
      ) {
        return;
      }

      if (!invoiceItemId) {
        if (amountCents > 0 && description) {
          createItem({
            invoiceId: draftInvoiceId,
            amountCents,
            currency: currency.toLowerCase(),
            description,
          });
          prevAmount.current = amountCents;
          prevDescription.current = description;
        }
      } else {
        patchItem({
          invoiceItemId,
          amountCents:
            amountCents !== prevAmount.current ? amountCents : undefined,
          description:
            description !== prevDescription.current ? description : undefined,
        });
        prevAmount.current = amountCents;
        prevDescription.current = description;
      }
    }, 300);
  };

  // Helper function to create a draft invoice (called when clicking Next for one-time invoices)
  const createDraftForOneTimeInvoice = async (): Promise<boolean> => {
    if (draftInvoiceId) return true; // Already have a draft
    if (isCreatingDraft) return false; // Already creating

      setIsCreatingDraft(true);

      const draftInvoiceData: any = {
        customerId,
        currency,
      };

      if (dueDate) {
        const dueDateToUse = new Date(
          Math.max(dueDate.getTime(), getEndOfToday().getTime()),
        );
        draftInvoiceData.dueDate = Math.floor(dueDateToUse.getTime() / 1000);
      }

    return new Promise((resolve) => {
      createDraftInvoice(draftInvoiceData)
        .then((data) => {
          setDraftInvoiceId(data.id);
          if (data.items && data.items.length > 0) {
            setInvoiceItemId(data.items[0].id);
            if (data.items[0].amount) {
              prevAmount.current = data.items[0].amount / 100;
              form.setValue("amount", data.items[0].amount / 100);
            }
            if (data.items[0].description) {
              prevDescription.current = data.items[0].description;
              form.setValue("description", data.items[0].description);
            }
          }
          setIsCreatingDraft(false);
          queryClient.invalidateQueries({ queryKey: [InvoicesKeys.Invoices] });
          toast.success("Draft invoice created");
          track("draft_invoice_created", {
            invoice_id: data.id,
            customer_id: data.customer,
            currency: data.currency,
          });
          // Update customerSelected state
          setCustomerSelected({ id: customerId } as Client);
          setCurrencySelected(currency.toUpperCase());
          resolve(true);
        })
        .catch(() => {
          setIsCreatingDraft(false);
          toast.error("Failed to create draft invoice");
          resolve(false);
        });
    });
  };

  // Update payment methods when currency changes
  useEffect(() => {
    if (selectedPaymentMethods.length > 0) {
      const validPaymentMethods = filterPaymentMethodsByCurrency(
        selectedPaymentMethods,
        currency,
      );
      if (validPaymentMethods.length !== selectedPaymentMethods.length) {
        form.setValue("paymentMethods", validPaymentMethods);
      }
    }
  }, [currency, selectedPaymentMethods, form]);

  // Clean up the timer on unmount
  useEffect(() => {
    return () => {
      if (itemBlurTimerRef.current) {
        clearTimeout(itemBlurTimerRef.current);
      }
    };
  }, []);

  /**
   * STA-682: sync every line item on the form to Stripe. Before this, only
   * the first line item was pushed — extras were silently dropped. Diffs the
   * form's current lineItems against the baseline snapshot (loaded from
   * Stripe) and issues create / patch / delete calls accordingly.
   *
   * Returns true on success, false if any call failed or validation failed.
   */
  const syncAllLineItems = async (): Promise<boolean> => {
    if (!draftInvoiceId) return false;
    const values = form.getValues();
    const currentItems = (values.lineItems || []).filter(
      (li) => li && (li.description?.trim() || Number(li.amount) > 0),
    );

    if (currentItems.length === 0) {
      toast.error(
        "Please add at least one line item with a description and amount.",
      );
      return false;
    }

    // All populated items must have both a description and a positive amount.
    for (const item of currentItems) {
      if (!item.description?.trim() || Number(item.amount) <= 0) {
        toast.error(
          "Every line item needs a description and a positive amount.",
        );
        return false;
      }
    }

    const baseline = baselineLineItemsRef.current;
    const currentIds = new Set(
      currentItems.map((li) => li.id).filter(Boolean) as string[],
    );
    const itemsToDelete = baseline.filter((b) => !currentIds.has(b.id));

    const currencyLower = values.currency.toLowerCase();
    const newBaseline: Array<{
      id: string;
      description: string;
      amount: number;
    }> = [];

    try {
      // Delete removed items first so Stripe's total reflects the new state.
      for (const removed of itemsToDelete) {
        await deleteInvoiceItem(removed.id);
      }

      // Create or patch each current item in order.
      for (let index = 0; index < currentItems.length; index += 1) {
        const item = currentItems[index];
        const amountCents = Math.round(Number(item.amount) * 100);
        const description = item.description.trim();

        if (item.id) {
          const prior = baseline.find((b) => b.id === item.id);
          const amountChanged =
            !prior || Math.round(prior.amount * 100) !== amountCents;
          const descriptionChanged =
            !prior || prior.description !== description;
          if (amountChanged || descriptionChanged) {
            await patchInvoiceItem(item.id, {
              amountCents: amountChanged ? amountCents : undefined,
              description: descriptionChanged ? description : undefined,
            });
          }
          newBaseline.push({
            id: item.id,
            description,
            amount: Number(item.amount),
          });
          // Keep the form's lineItems[index].id in sync with what we just
          // confirmed is on Stripe.
          form.setValue(`lineItems.${index}.id`, item.id);
        } else {
          const created = await createInvoiceItem({
            invoiceId: draftInvoiceId,
            amountCents,
            currency: currencyLower,
            description,
          });
          form.setValue(`lineItems.${index}.id`, created.id);
          newBaseline.push({
            id: created.id,
            description,
            amount: Number(item.amount),
          });
        }
      }
    } catch (error) {
      console.error("Failed to sync line items:", error);
      toast.error("Failed to save line items. Please try again.");
      return false;
    }

    baselineLineItemsRef.current = newBaseline;
    // Keep the single-item refs aligned to the first line so the existing
    // blur-autosave path doesn't re-fire stale data.
    prevDescription.current = newBaseline[0]?.description || "";
    prevAmount.current = Math.round((newBaseline[0]?.amount || 0) * 100);
    setInvoiceItemId(newBaseline[0]?.id || null);

    return true;
  };

  const handleSubmit = async (values: InvoiceFormValues) => {
    setIsSubmitting(true);

    // Handle recurring invoices
    if (invoiceType === "recurring") {
      if (!values.customerId) {
        toast.error("Please select a customer first.");
        setIsSubmitting(false);
        return;
      }

      // Find the full customer from the clients array to get the name
      const customer = clients.find((c) => c.id === values.customerId);
      const customerName =
        customer?.name ||
        customer?.email ||
        customerSelected?.name ||
        customerSelected?.email ||
        "Customer";

      toast.loading("Creating recurring invoice...", {
        id: "creating-recurring",
      });
      try {
        // STA-682: send every line item so the backend can create one Stripe
        // Product + subscription item per line. Every generated invoice will
        // render them as N lines with the founder's descriptions.
        const populatedLines = (values.lineItems || []).filter(
          (li) =>
            (li?.description?.trim() || "") !== "" && Number(li?.amount) > 0,
        );
        const lineItemsForApi = populatedLines.map((li) => ({
          description: li.description.trim(),
          amount: Math.round(Number(li.amount) * 100),
        }));
        const totalCents =
          lineItemsForApi.reduce((sum, li) => sum + li.amount, 0) ||
          Math.round((values.amount || 0) * 100);
        const primaryDescription =
          populatedLines[0]?.description?.trim() || values.description || "";

        if (lineItemsForApi.length === 0) {
          toast.dismiss("creating-recurring");
          toast.error(
            "Please add at least one line item with description and amount.",
          );
          return;
        }

        await createRecurringInvoiceMutation.mutateAsync({
          customerId: values.customerId,
          customerName,
          // Description is used as the subscription-level summary. We send
          // the first line description as a reasonable default; Stripe's
          // per-line rendering comes from the per-item products.
          description: primaryDescription,
          currency: values.currency,
          amount: totalCents,
          frequency: values.frequency || 1,
          frequencyUnit: values.frequencyUnit || "months",
          dueDateType: values.dueDateType || "immediate",
          dueDateDays: values.dueDateDays || 0,
          endCondition: values.endCondition || "never",
          endAfterCount:
            values.endCondition === "after_count" ? values.endAfterCount : null,
          poNumber: values.poNumber || undefined,
          projectName: values.projectName || undefined,
          notes: values.notes || undefined,
          lineItems: lineItemsForApi,
        });

        toast.dismiss("creating-recurring");
        setIsSubmitting(false);
        handleClose();
        if (onSubmit) {
          onSubmit();
        }
        return;
      } catch (error) {
        toast.dismiss("creating-recurring");
        setIsSubmitting(false);
        return;
      }
    }

    // Handle one-time invoices
    if (!draftInvoiceId && existingInvoiceId) {
      setDraftInvoiceId(existingInvoiceId);
    }

    if (!draftInvoiceId) {
      setIsSubmitting(false);
      toast.error(
        "No draft invoice created. Please select a customer and currency first.",
      );
      return;
    }

    if (itemBlurTimerRef.current) {
      clearTimeout(itemBlurTimerRef.current);
      itemBlurTimerRef.current = null;
    }

    const lineItemsOk = await syncAllLineItems();
    if (!lineItemsOk) {
      setIsSubmitting(false);
      return;
    }

    // STA-682: every submit sends PO / Project / Notes on both the create
    // (existing draft not yet finalized) and edit-existing-draft paths.
    // The backend treats them as optional — empty strings clear them on Stripe.
    const invoiceIdToPatch = existingInvoiceId || draftInvoiceId;
    toast.loading(existingInvoiceId ? "Updating invoice..." : "Saving invoice...", {
      id: existingInvoiceId ? "updating" : "saving-draft",
    });
    try {
      await patchInvoiceHeader(invoiceIdToPatch, {
        currency: values.currency,
        dueDate: values.dueDate
          ? Math.floor(values.dueDate.getTime() / 1000)
          : Math.floor((Date.now() + 30 * 24 * 60 * 60 * 1000) / 1000),
        paymentMethodTypes: values.paymentMethods,
        poNumber: values.poNumber ?? "",
        projectName: values.projectName ?? "",
        notes: values.notes ?? "",
      });
      toast.dismiss(existingInvoiceId ? "updating" : "saving-draft");
    } catch (error) {
      console.error("Failed to save invoice fields:", error);
      toast.error("Failed to save invoice. Please try again.", {
        id: existingInvoiceId ? "updating" : "saving-draft",
      });
      setIsSubmitting(false);
      return;
    }

    toast.loading("Finalizing invoice...", { id: "finalizing" });
    // Note: isSubmitting stays true through finalize. It's cleared by
    // the mutation's onSuccess/onError callbacks (which call handleClose
    // or show an error toast, both resetting form state).
    createInvoice(values);
  };

  const isInvoiceEditable = currentInvoice
    ? currentInvoice.status === "draft"
    : !existingInvoiceId;
  const invoiceStatus = currentInvoice?.status;
  const isReadOnlyView = Boolean(
    existingInvoiceId && invoiceStatus && invoiceStatus !== "draft",
  );
  const isPreviewable =
    invoiceStatus === "open" ||
    invoiceStatus === "paid" ||
    invoiceStatus === "sent";

  const isCustomerDisabled = !!existingInvoiceId;
  const isCurrencyDisabled = !customerSelected || !!existingInvoiceId;
  // In Step 1, amount is enabled once customer is selected
  // In Step 2, fields are disabled if no draft (for one-time) or invoice not editable
  const isAmountDisabled =
    !customerSelected || (existingInvoiceId && !isInvoiceEditable);
  const areDetailsDisabled =
    (existingInvoiceId && !isInvoiceEditable) ||
    (!existingInvoiceId && invoiceType !== "recurring" && !draftInvoiceId);
  const isDueDateDisabled = !customerSelected || invoiceType === "recurring";

  const onCancelClick = () => {
    form.reset();
    onOpenChange(false);
  };

  const handlePreviewInvoice = () => {
    const invoicePdfUrl =
      currentInvoice?.invoice_pdf ||
      existingInvoice?.invoice_pdf ||
      currentInvoice?.pdfUrl;
    if (invoicePdfUrl) {
      setIsPdfPreviewOpen(true);
      track("preview_invoice", {
        invoice_id: currentInvoice?.id,
        invoice_number: currentInvoice?.number,
      });
    } else {
      toast.error("Invoice PDF not available");
    }
  };

  const handleClose = () => {
    setDraftInvoiceId(null);
    setInvoiceItemId(null);
    setCustomerSelected(null);
    setCurrencySelected(null);
    setIsCreatingDraft(false);
    setIsCreatingLineItem(false);
    setIsSubmitting(false);
    setIsPdfPreviewOpen(false);
    setFinalizedInvoice(null);
    setExistingInvoiceId(undefined);
    setActiveTab("basics");
    prevAmount.current = 0;
    prevDescription.current = "";
    formValuesRef.current = null;

    if (itemBlurTimerRef.current) {
      clearTimeout(itemBlurTimerRef.current);
      itemBlurTimerRef.current = null;
    }

    form.reset({
      customerId: "",
      currency: "USD",
      amount: 0,
      description: "",
      dueDate: getEndOfToday(),
      paymentMethods: [],
      poNumber: "",
      projectName: "",
      lineItems: [{ description: "", amount: 0 }],
      invoiceType: "one-time",
      frequency: recurringConfig.frequency,
      frequencyUnit: recurringConfig.frequencyUnit,
      dueDateType: recurringConfig.dueDateType,
      dueDateDays: recurringConfig.dueDateDays,
      endCondition: recurringConfig.endCondition,
      endAfterCount: recurringConfig.endAfterCount,
    });
    setInvoiceType("one-time");

    onOpenChange(false);
  };

  // Step navigation
  const tabs = [
    { value: "basics" as const, label: "Basics" },
    { value: "details" as const, label: "Details" },
  ];
  const activeTabIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.value === activeTab),
  );
  const currentStepNumber = activeTabIndex + 1;
  const totalSteps = tabs.length;

  const subtitle = isReadOnlyView
    ? "Invoice summary"
    : `Create a new invoice · Step ${currentStepNumber} of ${totalSteps}`;
  const selectedCustomerDetails = clients.find(
    (client) => client.id === customerId,
  );
  const displayCustomerName =
    selectedCustomerDetails?.company ||
    selectedCustomerDetails?.name ||
    customerSelected?.name ||
    customerSelected?.email ||
    (customerSelected?.id ? `Customer ${customerSelected.id}` : "Customer");
  const displayCurrency = currency || "USD";
  const displayAmount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: displayCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount || 0);
  const invoicePdfUrl =
    currentInvoice?.invoice_pdf ||
    existingInvoice?.invoice_pdf ||
    currentInvoice?.pdfUrl;

  // Prepare footer content
  const getFooterContent = () => {
    if (!existingInvoiceId || isInvoiceEditable) {
      const isBasicsValid =
        !!customerId && !!currency && typeof amount === "number" && amount > 0;
      const isDetailsDisabled =
            !form.formState.isValid ||
            isPending ||
            isCreatingLineItem ||
        isPatchingItem ||
        createRecurringInvoiceMutation.isPending;
      const cancelLabel = activeTab === "details" ? "Back" : "Cancel";
      const submitLabel = activeTab === "basics" ? "Next" : "Create Invoice";

      const handleCancel = () => {
        if (activeTab === "details") {
          setActiveTab("basics");
          return;
        }
        onCancelClick();
      };

      const handlePrimaryAction = async () => {
        if (activeTab === "basics") {
          const isValid = await form.trigger([
            "customerId",
            "currency",
            "amount",
          ]);
          if (!isValid) return;

          // For one-time invoices, create draft before moving to step 2
          if (invoiceType === "one-time") {
            const draftCreated = await createDraftForOneTimeInvoice();
            if (!draftCreated) return;
          }

          setActiveTab("details");
          return;
        }
        form.handleSubmit(handleSubmit)();
      };

      return (
        <div className="flex items-center justify-end w-full gap-2">
            <Button type="button" variant="outline" onClick={handleCancel}>
              {cancelLabel}
            </Button>
            <Button
              type="button"
              onClick={handlePrimaryAction}
              disabled={
                isSubmitting ||
                (activeTab === "basics"
                  ? !isBasicsValid || isCreatingDraft
                  : isDetailsDisabled)
              }
              isLoading={
                isSubmitting ||
                (activeTab === "basics" && isCreatingDraft) ||
                (activeTab === "details" &&
                  (isPending || createRecurringInvoiceMutation.isPending))
              }
            >
              {submitLabel}
            </Button>
        </div>
      );
    } else if (isReadOnlyView) {
      return (
        <div className="flex justify-between items-center">
          <Button type="button" variant="outline" onClick={onCancelClick}>
            Close
          </Button>
          {isPreviewable && (
          <Button type="button" onClick={handlePreviewInvoice}>
            Preview Invoice
          </Button>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <InvoiceDrawer
        open={open}
        onOpenChange={handleClose}
        title="Invoice"
        subtitle={subtitle}
        footer={getFooterContent()}
      >
        {isLoadingInvoice && !finalizedInvoice ? (
          <div className="py-8 text-center space-y-4">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
            <p className="text-muted-foreground">Loading invoice details...</p>
          </div>
        ) : !currentInvoice && existingInvoiceId ? (
          <div className="py-8 text-center space-y-4">
            <div className="flex justify-center text-amber-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <p className="text-muted-foreground">
              Invoice data could not be loaded.
            </p>
            <p className="text-sm text-muted-foreground">
              The invoice might not exist or you don't have permission to view
              it.
            </p>
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              {/* Recurring Invoice Banner */}
              {currentInvoice?.isRecurring && currentInvoice?.subscriptionId && (
                <div className="bg-[#f9f9f9] border border-[#f1f1f1] rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white border border-[#e8e8e8]">
                      <RecurringArrow className="h-4 w-4 text-[#1e1e24]" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide font-semibold text-[#767677]">
                        Recurring Invoice
                      </div>
                      <div className="text-sm text-[#494949]">
                        This invoice is part of a recurring series.
                      </div>
                    </div>
                  </div>
                  {onViewRecurringInvoice && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        onViewRecurringInvoice(currentInvoice.subscriptionId)
                      }
                      className="text-sm font-semibold"
                    >
                      View
                    </Button>
                  )}
                </div>
              )}

              <Tabs
                value={activeTab}
                onValueChange={(value) =>
                  setActiveTab(value as "basics" | "details")
                }
                className="space-y-6"
              >
                <TabsContent value="basics" className="space-y-4">
              <CustomerInformation
                control={form.control}
                disabled={isCustomerDisabled}
              />

                  <CurrencySelector
                    control={form.control}
                    disabled={isCurrencyDisabled}
                    invoiceId={draftInvoiceId}
                    onCurrencyChange={() => {
                      if (
                        customerSelected &&
                        !currencySelected &&
                        !draftInvoiceId
                      ) {
                        setCurrencySelected(currency.toUpperCase());
                      }
                    }}
                  />

                  <LineItemsEditor
                    control={form.control}
                    // STA-682: must honour the read-only gate for finalized
                    // invoices (open / paid / void / uncollectible). Before
                    // this fix, clicking a finalized invoice showed an
                    // editable line-item editor because only the
                    // "customer not yet picked" half of the gate was wired.
                    disabled={isAmountDisabled}
                    onTotalChange={handleLineItemsTotalChange}
                  />

                  {!existingInvoiceId && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Invoice Type
                      </Label>
                      <RadioGroup
                        value={invoiceType}
                        onValueChange={(value) => {
                          const newType = value as "one-time" | "recurring";
                          setInvoiceType(newType);
                          form.setValue("invoiceType", newType);

                          if (newType === "recurring") {
                            form.setValue(
                              "frequency",
                              recurringConfig.frequency,
                            );
                            form.setValue(
                              "frequencyUnit",
                              recurringConfig.frequencyUnit,
                            );
                            form.setValue(
                              "dueDateType",
                              recurringConfig.dueDateType,
                            );
                            form.setValue(
                              "dueDateDays",
                              recurringConfig.dueDateDays,
                            );
                            form.setValue(
                              "endCondition",
                              recurringConfig.endCondition,
                            );
                            form.setValue(
                              "endAfterCount",
                              recurringConfig.endAfterCount,
                            );
                          } else {
                            const currentValues = form.getValues();
                            setRecurringConfig({
                              frequency: currentValues.frequency || 1,
                              frequencyUnit:
                                currentValues.frequencyUnit || "months",
                              dueDateType:
                                currentValues.dueDateType || "immediate",
                              dueDateDays: currentValues.dueDateDays || 0,
                              endCondition:
                                currentValues.endCondition || "never",
                              endAfterCount:
                                currentValues.endAfterCount || null,
                            });
                          }
                        }}
                        className="flex gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="one-time" id="one-time" />
                          <Label
                            htmlFor="one-time"
                            className="font-normal cursor-pointer"
                          >
                            One-time
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="recurring" id="recurring" />
                          <Label
                            htmlFor="recurring"
                            className="font-normal cursor-pointer"
                          >
                            Recurring
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="details" className="!mt-0 space-y-6">
                  {/* Summary header */}
                  <div className="bg-gray-50 border-b border-gray-100 py-4 px-6 -mx-6 -mt-6 mb-2 flex items-center gap-6 flex-wrap">
                    <div className="flex flex-col">
                      <span className="text-xs uppercase tracking-wide font-semibold text-gray-500">
                        Customer
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {displayCustomerName}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs uppercase tracking-wide font-semibold text-gray-500">
                        Amount
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {displayAmount}
                      </span>
                    </div>
                    {/* STA-682: Surface PO / Project in the summary when set.
                        Notes intentionally not shown here since they can be
                        long; they render as a read-only textarea below. */}
                    {form.watch("projectName") ? (
                      <div className="flex flex-col">
                        <span className="text-xs uppercase tracking-wide font-semibold text-gray-500">
                          Project
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {form.watch("projectName")}
                        </span>
                      </div>
                    ) : null}
                    {form.watch("poNumber") ? (
                      <div className="flex flex-col">
                        <span className="text-xs uppercase tracking-wide font-semibold text-gray-500">
                          PO Number
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {form.watch("poNumber")}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  {/* ── Invoice Details ── */}
                  <div className="space-y-4">
                    <h4 className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                      Invoice Details
                    </h4>

                    <div className="grid grid-cols-1 gap-4">
                      <InvoiceCustomFields
                        control={form.control}
                        disabled={areDetailsDisabled}
                      />
                    </div>

                    {invoiceType !== "recurring" && (
                      <DueDatePicker
                        control={form.control}
                        disabled={isDueDateDisabled || areDetailsDisabled}
                        invoiceId={draftInvoiceId}
                      />
                    )}

                    {invoiceType === "recurring" && !existingInvoiceId && (
                      <RecurringInvoiceConfig
                        control={form.control}
                        disabled={false}
                      />
                    )}

                    <InvoiceNotesField
                      control={form.control}
                      disabled={areDetailsDisabled}
                    />
                  </div>

                  {invoiceType === "one-time" && (
                    <PaymentMethodSelector
                      control={form.control}
                      availablePaymentMethods={paymentMethods}
                      disabled={areDetailsDisabled}
                      invoiceId={draftInvoiceId}
                    />
                  )}
                </TabsContent>
              </Tabs>
            </form>
          </Form>
        )}
      </InvoiceDrawer>

      {invoicePdfUrl && (
        <InvoicePdfPreviewSheet
          open={isPdfPreviewOpen}
          onOpenChange={setIsPdfPreviewOpen}
          pdfUrl={invoicePdfUrl}
          invoiceNumber={currentInvoice?.number}
          invoiceId={currentInvoice?.id}
          invoiceStatus={currentInvoice?.status}
        />
      )}
    </>
  );
};

export default InvoiceFormSheet;
